import "../location-type-options/location-type-options.css";

import { Checkbox } from "@tql/ui-components-react/tql/inputs/beta";

import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";
import type { Accessorial } from "~/shared/schemas/accessorial-schema";
import type { SubGroupKey } from "~/shared/types/quote-details-types";

import {
	APPOINTMENT_SERVICE_NAME,
	RESIDENTIAL_SERVICE_NAME,
	usePickupDeliveryDetails,
} from "../../hooks/use-pickup-delivery-details.ts";
import { ServiceOptionsMobile } from "./service-options-mobile.tsx";

const SERVICE_DISPLAY_NAMES: Record<string, string> = {
	"Delivery Call Ahead": "Call Ahead",
};

type ServiceOptionsProps = {
	name: SubGroupKey;
	isOptionSelected: (option: Accessorial) => boolean;
	handleCombinedSelectionChange: (
		option: Accessorial,
		checked: boolean,
	) => void;
	disabled?: boolean;
	/** Optional: provide service options directly. If not provided, will use context. */
	serviceOptions?: Accessorial[];
	/** Optional: provide location type options directly (needed for residential check). If not provided, will use context. */
	locationTypeOptions?: Accessorial[];
	/** Always render the checkbox layout, even when `useIsMobile()` is true. */
	forceDesktopLayout?: boolean;
};

export const ServiceOptions = ({
	name,
	isOptionSelected,
	handleCombinedSelectionChange,
	disabled,
	serviceOptions: serviceOptionsProp,
	locationTypeOptions: locationTypeOptionsProp,
	forceDesktopLayout = false,
}: ServiceOptionsProps) => {
	const isMobile = useIsMobile() && !forceDesktopLayout;

	// Use provided options or fall back to context
	const contextData = usePickupDeliveryDetails(name);
	const serviceOptions = serviceOptionsProp ?? contextData.services[name];
	const locationTypeOptions =
		locationTypeOptionsProp ?? contextData.locationTypeOptions;

	const residentialOption = locationTypeOptions.find(
		(option) => option.name === RESIDENTIAL_SERVICE_NAME,
	);

	// !! keeps this a strict boolean (never undefined) so isOptionDisabled below can safely use ||.
	const isResidentialSelected =
		name === "delivery" &&
		!!residentialOption &&
		isOptionSelected(residentialOption);

	const isOptionDisabled = (option: Accessorial) =>
		(option.name === APPOINTMENT_SERVICE_NAME && isResidentialSelected) ||
		!!disabled;

	if (isMobile) {
		return (
			<ServiceOptionsMobile
				handleCombinedSelectionChange={handleCombinedSelectionChange}
				isOptionDisabled={isOptionDisabled}
				isOptionSelected={isOptionSelected}
				name={name}
				serviceOptions={serviceOptions ?? []}
			/>
		);
	}

	return (
		<div
			className={"combined-services-container"}
			data-testid={"service-options"}
		>
			{serviceOptions?.map((option) => {
				const displayName = SERVICE_DISPLAY_NAMES[option.name] ?? option.name;

				return (
					<div key={option.id} className={"tql-checkbox-item"}>
						<Checkbox
							aria-label={displayName}
							checked={isOptionSelected(option)}
							disabled={isOptionDisabled(option)}
							id={`${name}-option-${option.id}`}
							onChange={(event) =>
								handleCombinedSelectionChange(option, event.value)
							}
							tabIndex={-1}
						/>
						<div
							onClick={() =>
								handleCombinedSelectionChange(option, !isOptionSelected(option))
							}
							onKeyDown={(event) => {
								if (event.key === "Enter" || event.key === " ") {
									event.preventDefault();
									handleCombinedSelectionChange(
										option,
										!isOptionSelected(option),
									);
								}
							}}
							role={"button"}
							style={{ cursor: "pointer", userSelect: "none" }}
							tabIndex={-1}
						>
							{displayName}
						</div>
					</div>
				);
			})}
		</div>
	);
};
